import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Button,
  TextField,
  Select,
  MenuItem,
  InputAdornment,
  Stack,
  Tooltip,
  Avatar,
  Alert,
  Badge,
} from '@mui/material';
import {
  Business,
  People,
  AccountTree,
  Insights,
  ErrorOutline,
  ExpandMore,
  Search,
  TrendingUp,
  WarningAmber,
  CheckCircle,
  Public,
  Dashboard as DashboardIcon,
  Assessment,
} from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import OwnershipGraph from '../components/OwnershipGraph/OwnershipGraph';
import JurisdictionBadge from '../components/JurisdictionBadge/JurisdictionBadge';
import { companiesAPI, personsAPI } from '../services/api';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { WorldMap } from 'react-svg-worldmap';
import { getJurisdictionColor, lightenColor } from '../utils/jurisdictionColors';

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [serverInsights, setServerInsights] = useState<any | null>(null);
  const [windUpGroups, setWindUpGroups] = useState<Array<{ jurisdiction: string; companies: any[] }>>([]);
  const navigate = useNavigate();
  const [hoveredJurisdiction, setHoveredJurisdiction] = useState<string | null>(null);
  // Key Players filters/paging
  const [kpSearch, setKpSearch] = useState('');
  const [kpType, setKpType] = useState<'all' | 'person' | 'company'>('all');
  const [kpRole, setKpRole] = useState<'ALL' | 'DIRECTOR' | 'SHAREHOLDER'>('ALL');
  const [kpPage, setKpPage] = useState(1);
  const [kpLimit] = useState(10);
  const [kpLoading, setKpLoading] = useState(false);
  const [kpData, setKpData] = useState<{ data: any[]; meta: { total: number; page: number; limit: number; totalPages: number } } | null>(null);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const statsRes = await companiesAPI.getStats();
      setStats(statsRes.data);
      const insightsRes = await companiesAPI.getInsights();
      setServerInsights(insightsRes.data);
      const windupRes = await companiesAPI.getWindUp();
      setWindUpGroups(Array.isArray(windupRes.data) ? windupRes.data : []);
    } catch (err: any) {
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  };

  // Debounced fetch for Key Players
  useEffect(() => {
    const handle = setTimeout(async () => {
      try {
        setKpLoading(true);
        const res = await companiesAPI.getTopOwners({ search: kpSearch || undefined, type: kpType, role: kpRole, page: kpPage, limit: kpLimit });
        setKpData(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setKpLoading(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [kpSearch, kpType, kpRole, kpPage, kpLimit]);

  // Derived insights computed on client (fallback if serverInsights missing)
  const insights = useMemo(() => {
    if (loading) {
      return {
        companiesWithNoOwnership: [],
        companiesMissingRegNo: [],
        companiesMissingIncorpDate: [],
        ownershipCompletenessIssues: [],
        topOwners: [],
      };
    }
    if (serverInsights) return serverInsights;
    return {
      companiesWithNoOwnership: [],
      companiesMissingRegNo: [],
      companiesMissingIncorpDate: [],
      ownershipCompletenessIssues: [],
      topOwners: [],
    };
  }, [loading, serverInsights]);

  const keyPlayers = useMemo(() => {
    return (kpData?.data && kpData.data.length > 0) ? kpData.data : (insights.topOwners || []);
  }, [kpData, insights]);

  const kpMeta = useMemo(() => {
    if (kpData?.meta) return kpData.meta;
    const total = Array.isArray(keyPlayers) ? keyPlayers.length : 0;
    return { total, page: 1, limit: total || 1, totalPages: 1 };
  }, [kpData, keyPlayers]);

  // Quick per-jurisdiction data-quality counts for infographic-style indicators
  const jurisdictionDQMap = useMemo(() => {
    const map = new Map<string, { regNoMissing: number; incorpDateMissing: number; ownershipPctIssue: number }>();
    if (loading || !stats?.byJurisdiction) return map;
    (stats.byJurisdiction as Array<any>).forEach((item: any) => {
      const j = item.jurisdiction;
      map.set(j, {
        regNoMissing: insights.companiesMissingRegNo.filter((c: any) => c.jurisdiction === j).length,
        incorpDateMissing: insights.companiesMissingIncorpDate.filter((c: any) => c.jurisdiction === j).length,
        ownershipPctIssue: insights.ownershipCompletenessIssues.filter((c: any) => c.jurisdiction === j).length,
      });
    });
    return map;
  }, [loading, stats, insights]);

  // Map jurisdiction names to ISO2 country codes for world map - simplified
  const jurisdictionToISO2 = (name: string | null | undefined): string | null => {
    if (!name) return null;
    const n = name.trim().toUpperCase().replace(/\s+/g, ' ');
    const mapping: Record<string, string> = {
      'UNITED KINGDOM': 'gb',
      'UK': 'gb',
      'UK OLD': 'gb',
      'ISLE OF MAN': 'im',
      'BVI': 'vg',
      'B V I': 'vg',
      'UAE': 'ae',
      'UNITED ARAB EMIRATES': 'ae',
      'CANADA': 'ca',
      'INDIA': 'in',
      'TANZANIA': 'tz',
      'TANZANIA ASMA': 'tz',
      'DRC': 'cd',
      'ZAMBIA': 'zm',
      'ZAMBIA M': 'zm',
      'ZAMBIA OLD': 'zm',
      'MOZAMBIQUE': 'mz',
      'MALAWI': 'mw',
      'UGANDA': 'ug',
      'KENYA': 'ke',
      'ZIMBABWE': 'zw',
      'ZIMBABWE OLD': 'zw',
      'ANGOLA': 'ao',
    };
    return mapping[n] || null;
  };

  const { mapData, codeToBestJurisdiction } = useMemo(() => {
    const result: Array<{ country: string; value: number }> = [];
    const codeToValue = new Map<string, number>();
    const codeToJurisdiction = new Map<string, string>();
    if (!stats?.byJurisdiction) return { mapData: result, codeToBestJurisdiction: codeToJurisdiction };
    (stats.byJurisdiction as Array<any>).forEach((item: any) => {
      const code = jurisdictionToISO2(item.jurisdiction);
      if (!code) return;
      const current = codeToValue.get(code) || 0;
      const newValue = current + (item.count || 0);
      codeToValue.set(code, newValue);
      const existingJurisdiction = codeToJurisdiction.get(code);
      if (!existingJurisdiction) {
        codeToJurisdiction.set(code, item.jurisdiction);
      } else {
        const existingItem = (stats.byJurisdiction as Array<any>).find((j: any) => j.jurisdiction === existingJurisdiction);
        if (!existingItem || (item.count || 0) > (existingItem.count || 0)) {
          codeToJurisdiction.set(code, item.jurisdiction);
        }
      }
    });
    codeToValue.forEach((value, country) => result.push({ country, value }));
    return { mapData: result, codeToBestJurisdiction: codeToJurisdiction };
  }, [stats]);

  // Map highlight styling based on hovered jurisdiction from the list
  const hoveredISO = useMemo(() => jurisdictionToISO2(hoveredJurisdiction), [hoveredJurisdiction]);
  const mapStyleFunction = useMemo(() => {
    return (context: any) => {
      const code = (context.countryCode || context.country || '').toLowerCase();
      const isHovered = hoveredISO && code === hoveredISO;
      return {
        fill: isHovered ? '#FF9800' : context.color,
        stroke: isHovered ? '#333' : '#fff',
        strokeWidth: isHovered ? 1.5 : 0.5,
        cursor: 'pointer',
        transition: 'fill 0.15s ease, stroke 0.15s ease',
      } as React.CSSProperties;
    };
  }, [hoveredISO]);

  const totalDataQualityIssues = useMemo(() => {
    return insights.companiesWithNoOwnership.length +
           insights.companiesMissingRegNo.length +
           insights.companiesMissingIncorpDate.length +
           insights.ownershipCompletenessIssues.length;
  }, [insights]);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
              <DashboardIcon sx={{ fontSize: 32, color: 'white' }} />
            </Avatar>
            <Box>
              <Typography variant="h4" sx={{ color: 'white', fontWeight: 600 }}>
                AJ Group Holdings
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                Global Corporate Governance Dashboard
              </Typography>
            </Box>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Browse all companies">
              <Button
                component={RouterLink}
                to="/regions"
                variant="contained"
                startIcon={<Business />}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                }}
              >
                Companies
              </Button>
            </Tooltip>
            <Tooltip title="Browse all people">
              <Button
                component={RouterLink}
                to="/admin"
                variant="contained"
                startIcon={<People />}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                }}
              >
                People
              </Button>
            </Tooltip>
            <Tooltip title="View detailed data quality metrics">
              <Button
                component={RouterLink}
                to="/data-quality"
                variant="outlined"
                startIcon={<Assessment />}
                sx={{
                  borderColor: 'rgba(255,255,255,0.5)',
                  color: 'white',
                  '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                }}
              >
                Quality Report
              </Button>
            </Tooltip>
            <Tooltip title="Administrative settings">
              <Button
                component={RouterLink}
                to="/admin"
                variant="outlined"
                sx={{
                  borderColor: 'rgba(255,255,255,0.5)',
                  color: 'white',
                  '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                }}
              >
                Admin
              </Button>
            </Tooltip>
          </Stack>
        </Box>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', position: 'relative', overflow: 'visible' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box flex={1}>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    Total Companies
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {loading ? <CircularProgress size={32} /> : stats?.totalCompanies || 0}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                  <Business sx={{ fontSize: 28 }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box flex={1}>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    Jurisdictions
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 600, color: 'success.main' }}>
                    {loading ? <CircularProgress size={32} /> : stats?.byJurisdiction?.length || 0}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'success.main', width: 56, height: 56 }}>
                  <Public sx={{ fontSize: 28 }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box flex={1}>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    Active Companies
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 600, color: 'info.main' }}>
                    {loading ? (
                      <CircularProgress size={32} />
                    ) : (
                      stats?.byStatus?.find((s: any) => s.status === 'ACTIVE')?.count || 0
                    )}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'info.main', width: 56, height: 56 }}>
                  <CheckCircle sx={{ fontSize: 28 }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box flex={1}>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    Wind Up Flagged
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 600, color: 'error.main' }}>
                    {loading ? (
                      <CircularProgress size={32} />
                    ) : (
                      windUpGroups.reduce((sum, g) => sum + g.companies.length, 0)
                    )}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'error.main', width: 56, height: 56 }}>
                  <ErrorOutline sx={{ fontSize: 28 }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Data Quality Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>Missing Ownership</Typography>
                  <Typography variant="h4" fontWeight={600}>
                    {loading ? <CircularProgress size={24} sx={{ color: 'inherit' }} /> : insights.companiesWithNoOwnership.length}
                  </Typography>
                </Box>
                <WarningAmber sx={{ fontSize: 40, opacity: 0.8 }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>Missing Reg No.</Typography>
                  <Typography variant="h4" fontWeight={600}>
                    {loading ? <CircularProgress size={24} sx={{ color: 'inherit' }} /> : insights.companiesMissingRegNo.length}
                  </Typography>
                </Box>
                <ErrorOutline sx={{ fontSize: 40, opacity: 0.8 }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'info.light', color: 'info.contrastText' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>Missing Incorp Date</Typography>
                  <Typography variant="h4" fontWeight={600}>
                    {loading ? <CircularProgress size={24} sx={{ color: 'inherit' }} /> : insights.companiesMissingIncorpDate.length}
                  </Typography>
                </Box>
                <Insights sx={{ fontSize: 40, opacity: 0.8 }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'secondary.light', color: 'secondary.contrastText' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>Incomplete Ownership %</Typography>
                  <Typography variant="h4" fontWeight={600}>
                    {loading ? <CircularProgress size={24} sx={{ color: 'inherit' }} /> : insights.ownershipCompletenessIssues.length}
                  </Typography>
                </Box>
                <AccountTree sx={{ fontSize: 40, opacity: 0.8 }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Wind Up Tracker Banner */}
      {!loading && windUpGroups.reduce((sum, g) => sum + g.companies.length, 0) > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 4 }}
          action={
            <Button component={RouterLink} to="/wind-up" color="inherit" size="small">
              View Details
            </Button>
          }
        >
          <Typography variant="body2">
            <strong>{windUpGroups.reduce((sum, g) => sum + g.companies.length, 0)} companies</strong> have been flagged for wind up review.
          </Typography>
        </Alert>
      )}

      {/* Ownership Graph - Collapsible */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Accordion defaultExpanded={false}>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                borderRadius: '4px 4px 0 0',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
                '& .MuiAccordionSummary-expandIconWrapper': {
                  color: 'white',
                },
              }}
            >
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccountTree />
                Corporate Ownership Structure
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              {/* Jurisdiction Color Legend */}
              {stats?.byJurisdiction && stats.byJurisdiction.length > 0 && (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Public fontSize="small" />
                    Jurisdiction Colors Reference
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>
                    {[...stats.byJurisdiction]
                      .sort((a: any, b: any) => b.count - a.count)
                      .slice(0, 12)
                      .map((item: any) => {
                        const jurisdictionColor = getJurisdictionColor(item.jurisdiction);
                        return (
                          <Tooltip key={item.jurisdiction} title={`${item.count} ${item.count === 1 ? 'company' : 'companies'}`}>
                            <Chip
                              size="small"
                              label={item.jurisdiction}
                              sx={{
                                bgcolor: lightenColor(jurisdictionColor, 0.9),
                                borderLeft: `3px solid ${jurisdictionColor}`,
                                fontWeight: 500,
                                '&:hover': {
                                  bgcolor: lightenColor(jurisdictionColor, 0.8),
                                },
                              }}
                              icon={
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: jurisdictionColor,
                                    ml: 1,
                                  }}
                                />
                              }
                            />
                          </Tooltip>
                        );
                      })}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                    Node colors in the graph below correspond to company jurisdictions
                  </Typography>
                </Box>
              )}

              <OwnershipGraph height="700px" />
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>

      {/* Key Players and Data Quality Alerts */}
      {!loading && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Key Players - Enhanced */}
          <Grid item xs={12} md={6}>
            <Paper
              sx={{
                p: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                background: 'linear-gradient(to bottom, #ffffff 0%, #f8f9fa 100%)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                    <TrendingUp />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                      Key Players
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Top stakeholders by holdings
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  label={kpMeta.total}
                  color="primary"
                  sx={{ fontWeight: 600, minWidth: 50 }}
                />
              </Box>
              <Divider sx={{ mb: 2 }} />

              {/* Filters */}
              <Stack spacing={1.5} sx={{ mb: 2 }}>
                <TextField
                  size="small"
                  placeholder="Search people or companies..."
                  value={kpSearch}
                  onChange={(e) => { setKpPage(1); setKpSearch(e.target.value); }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'background.paper',
                    }
                  }}
                />
                <Stack direction="row" spacing={1}>
                  <Select
                    size="small"
                    value={kpType}
                    onChange={(e) => { setKpPage(1); setKpType(e.target.value as any); }}
                    sx={{
                      flex: 1,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <MenuItem value="all">All Types</MenuItem>
                    <MenuItem value="person">Persons</MenuItem>
                    <MenuItem value="company">Companies</MenuItem>
                  </Select>
                  <Select
                    size="small"
                    value={kpRole}
                    onChange={(e) => { setKpPage(1); setKpRole(e.target.value as any); }}
                    disabled={kpType === 'company'}
                    sx={{
                      flex: 1,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <MenuItem value="ALL">All Roles</MenuItem>
                    <MenuItem value="DIRECTOR">Directors</MenuItem>
                    <MenuItem value="SHAREHOLDER">Shareholders</MenuItem>
                  </Select>
                </Stack>
              </Stack>

              {/* Content */}
              {kpLoading ? (
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={6}>
                  <CircularProgress size={40} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Loading key players...
                  </Typography>
                </Box>
              ) : keyPlayers.length === 0 ? (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 6,
                    textAlign: 'center',
                  }}
                >
                  <Avatar sx={{ width: 64, height: 64, bgcolor: 'grey.100', mb: 2 }}>
                    <People sx={{ fontSize: 32, color: 'text.secondary' }} />
                  </Avatar>
                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    No key players found
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Try adjusting your search or filters
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                  <Stack spacing={1.5}>
                    {keyPlayers.map((o: any) => {
                      const isPerson = o.type === 'person';
                      const initials = isPerson
                        ? o.name?.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase() || '?'
                        : o.name?.slice(0, 2).toUpperCase() || '??';

                      return (
                        <Card
                          key={`${o.type}:${o.id || o.name}`}
                          component={isPerson && o.id ? RouterLink : 'div'}
                          to={isPerson && o.id ? `/persons/${encodeURIComponent(o.id)}` : undefined}
                          sx={{
                            p: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'background.paper',
                            textDecoration: 'none',
                            transition: 'all 0.2s ease-in-out',
                            cursor: isPerson && o.id ? 'pointer' : 'default',
                            '&:hover': isPerson && o.id ? {
                              borderColor: 'primary.main',
                              boxShadow: 2,
                              transform: 'translateY(-2px)',
                            } : {},
                          }}
                        >
                          <Stack direction="row" spacing={2} alignItems="center">
                            {/* Avatar */}
                            <Avatar
                              src={isPerson && o.profilePhotoUrl && o.id ? personsAPI.getProfilePhotoUrl(o.id) : undefined}
                              sx={{
                                width: 48,
                                height: 48,
                                bgcolor: isPerson ? 'primary.main' : 'secondary.main',
                                fontWeight: 600,
                                fontSize: '1rem',
                              }}
                            >
                              {isPerson ? initials : <Business />}
                            </Avatar>

                            {/* Content */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="body1"
                                fontWeight={600}
                                sx={{
                                  mb: 0.5,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {o.name}
                              </Typography>
                              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                <Chip
                                  label={isPerson ? (Array.isArray(o.personTypes) && o.personTypes.length > 0 ? o.personTypes.join(', ') : 'Person') : 'Company'}
                                  size="small"
                                  variant="outlined"
                                  color={isPerson ? 'primary' : 'secondary'}
                                  sx={{ height: 22 }}
                                />
                                {o.email && (
                                  <Chip
                                    label={o.email}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 22 }}
                                  />
                                )}
                                {o.nationality && (
                                  <Chip
                                    label={o.nationality}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 22 }}
                                  />
                                )}
                              </Stack>
                            </Box>

                            {/* Holdings Badge */}
                            <Box sx={{ textAlign: 'center', minWidth: 60 }}>
                              <Typography
                                variant="h5"
                                fontWeight={700}
                                color="primary.main"
                                sx={{ lineHeight: 1 }}
                              >
                                {o.count}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {o.count === 1 ? 'holding' : 'holdings'}
                              </Typography>
                            </Box>
                          </Stack>
                        </Card>
                      );
                    })}
                  </Stack>
                </Box>
              )}

              {/* Pagination */}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mt={2}
                pt={2}
                borderTop={1}
                borderColor="divider"
                sx={{ bgcolor: 'background.paper', borderRadius: 1, px: 1.5, py: 1 }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight={500}>
                  Page {kpMeta.page} of {kpMeta.totalPages} • {kpMeta.total} total
                </Typography>
                {kpData?.meta && (
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      disabled={kpMeta.page <= 1}
                      onClick={() => setKpPage((p) => Math.max(1, p - 1))}
                      variant="outlined"
                    >
                      Prev
                    </Button>
                    <Button
                      size="small"
                      disabled={kpMeta.page >= kpMeta.totalPages}
                      onClick={() => setKpPage((p) => p + 1)}
                      variant="outlined"
                    >
                      Next
                    </Button>
                  </Stack>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Data Quality Alerts */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Insights color="primary" />
                Data Quality Alerts
                <Badge badgeContent={totalDataQualityIssues} color="error" max={999}>
                  <Box sx={{ width: 24 }} />
                </Badge>
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ flex: 1, overflow: 'auto' }}>
                <Accordion disableGutters elevation={0}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography sx={{ flex: 1 }}>Companies Missing Ownership</Typography>
                    <Chip label={insights.companiesWithNoOwnership.length} size="small" color="warning" />
                  </AccordionSummary>
                  <AccordionDetails>
                    {insights.companiesWithNoOwnership.length === 0 ? (
                      <Alert severity="success">No issues found!</Alert>
                    ) : (
                      <List dense>
                        {insights.companiesWithNoOwnership.slice(0, 10).map((c: any) => (
                          <React.Fragment key={c.id}>
                            <ListItem
                              button
                              component={RouterLink}
                              to={`/companies/${encodeURIComponent(c.id)}`}
                              sx={{ borderRadius: 1 }}
                            >
                              <ListItemText
                                primary={c.name}
                                secondary={<JurisdictionBadge jurisdiction={c.jurisdiction} size="small" variant="outlined" />}
                              />
                            </ListItem>
                            <Divider />
                          </React.Fragment>
                        ))}
                        {insights.companiesWithNoOwnership.length > 10 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                            +{insights.companiesWithNoOwnership.length - 10} more
                          </Typography>
                        )}
                      </List>
                    )}
                  </AccordionDetails>
                </Accordion>

                <Accordion disableGutters elevation={0}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography sx={{ flex: 1 }}>Missing Registration No.</Typography>
                    <Chip color="error" label={insights.companiesMissingRegNo.length} size="small" />
                  </AccordionSummary>
                  <AccordionDetails>
                    {insights.companiesMissingRegNo.length === 0 ? (
                      <Alert severity="success">No issues found!</Alert>
                    ) : (
                      <List dense>
                        {insights.companiesMissingRegNo.slice(0, 10).map((c: any) => (
                          <React.Fragment key={c.id}>
                            <ListItem
                              button
                              component={RouterLink}
                              to={`/companies/${encodeURIComponent(c.id)}`}
                              sx={{ borderRadius: 1 }}
                            >
                              <ListItemText primary={c.name} />
                            </ListItem>
                            <Divider />
                          </React.Fragment>
                        ))}
                        {insights.companiesMissingRegNo.length > 10 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                            +{insights.companiesMissingRegNo.length - 10} more
                          </Typography>
                        )}
                      </List>
                    )}
                  </AccordionDetails>
                </Accordion>

                <Accordion disableGutters elevation={0}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography sx={{ flex: 1 }}>Missing Incorporation Date</Typography>
                    <Chip color="warning" label={insights.companiesMissingIncorpDate.length} size="small" />
                  </AccordionSummary>
                  <AccordionDetails>
                    {insights.companiesMissingIncorpDate.length === 0 ? (
                      <Alert severity="success">No issues found!</Alert>
                    ) : (
                      <List dense>
                        {insights.companiesMissingIncorpDate.slice(0, 10).map((c: any) => (
                          <React.Fragment key={c.id}>
                            <ListItem
                              button
                              component={RouterLink}
                              to={`/companies/${encodeURIComponent(c.id)}`}
                              sx={{ borderRadius: 1 }}
                            >
                              <ListItemText primary={c.name} />
                            </ListItem>
                            <Divider />
                          </React.Fragment>
                        ))}
                        {insights.companiesMissingIncorpDate.length > 10 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                            +{insights.companiesMissingIncorpDate.length - 10} more
                          </Typography>
                        )}
                      </List>
                    )}
                  </AccordionDetails>
                </Accordion>

                <Accordion disableGutters elevation={0}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography sx={{ flex: 1 }}>Incomplete Ownership %</Typography>
                    <Chip color="info" label={insights.ownershipCompletenessIssues.length} size="small" />
                  </AccordionSummary>
                  <AccordionDetails>
                    {insights.ownershipCompletenessIssues.length === 0 ? (
                      <Alert severity="success">No issues found!</Alert>
                    ) : (
                      <List dense>
                        {insights.ownershipCompletenessIssues.slice(0, 10).map((c: any) => (
                          <React.Fragment key={c.id}>
                            <ListItem
                              button
                              component={RouterLink}
                              to={`/companies/${encodeURIComponent(c.id)}`}
                              sx={{ borderRadius: 1 }}
                            >
                              <ListItemText
                                primary={c.name}
                                secondary={`Sum: ${c.sumPct ?? '—'}%`}
                              />
                            </ListItem>
                            <Divider />
                          </React.Fragment>
                        ))}
                        {insights.ownershipCompletenessIssues.length > 10 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                            +{insights.ownershipCompletenessIssues.length - 10} more
                          </Typography>
                        )}
                      </List>
                    )}
                  </AccordionDetails>
                </Accordion>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Jurisdiction Breakdown */}
      {stats?.byJurisdiction && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Public color="primary" />
                Companies by Jurisdiction
                <Chip label={stats.byJurisdiction.length} size="small" color="primary" />
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={3}>
                <Grid item xs={12} md={7}>
                  <Box sx={{ width: '100%', overflowX: 'auto', minHeight: 400 }}>
                    <WorldMap
                      color="rgba(25, 118, 210, 0.85)"
                      valueSuffix=" companies"
                      size="responsive"
                      data={mapData}
                      tooltipBgColor="#333"
                      tooltipTextColor="#fff"
                      styleFunction={mapStyleFunction}
                      onClickFunction={(event: any, countryName: string, isoCode: string) => {
                        const j = codeToBestJurisdiction.get(isoCode.toLowerCase());
                        if (j) {
                          navigate(`/regions?jurisdiction=${encodeURIComponent(j)}`);
                        }
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={5}>
                  <Grid container spacing={2}>
                    {[...stats.byJurisdiction]
                      .sort((a: any, b: any) => b.count - a.count)
                      .slice(0, 12)
                      .map((item: any) => {
                        const jurisdictionColor = getJurisdictionColor(item.jurisdiction);
                        const lightColor = lightenColor(jurisdictionColor, 0.92);
                        const dq = jurisdictionDQMap.get(item.jurisdiction) || { regNoMissing: 0, incorpDateMissing: 0, ownershipPctIssue: 0 };
                        const totalIssues = dq.regNoMissing + dq.incorpDateMissing + dq.ownershipPctIssue;

                        return (
                          <Grid item xs={12} key={item.jurisdiction}>
                            <Box
                              component={RouterLink}
                              to={`/regions?jurisdiction=${encodeURIComponent(item.jurisdiction)}`}
                              sx={{
                                p: 2,
                                bgcolor: lightColor,
                                borderRadius: 2,
                                borderLeft: `4px solid ${jurisdictionColor}`,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                color: 'inherit',
                                textDecoration: 'none',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  bgcolor: lightenColor(jurisdictionColor, 0.85),
                                  transform: 'translateX(4px)',
                                  boxShadow: 3,
                                },
                              }}
                              onMouseEnter={() => setHoveredJurisdiction(item.jurisdiction)}
                              onMouseLeave={() => setHoveredJurisdiction((current) => (current === item.jurisdiction ? null : current))}
                            >
                              <Box display="flex" alignItems="center" gap={1.5}>
                                <Box
                                  sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: '50%',
                                    bgcolor: jurisdictionColor,
                                    flexShrink: 0,
                                  }}
                                />
                                <Box>
                                  <Typography variant="body2" fontWeight={600}>
                                    {item.jurisdiction}
                                  </Typography>
                                  {totalIssues > 0 && (
                                    <Typography variant="caption" color="text.secondary">
                                      {totalIssues} data quality {totalIssues === 1 ? 'issue' : 'issues'}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                              <Box display="flex" alignItems="center" gap={1.5}>
                                {totalIssues > 0 && (
                                  <Stack direction="row" spacing={0.5}>
                                    {dq.regNoMissing > 0 && (
                                      <Tooltip title={`${dq.regNoMissing} missing registration numbers`}>
                                        <Chip label={dq.regNoMissing} size="small" color="error" sx={{ minWidth: 32, height: 20 }} />
                                      </Tooltip>
                                    )}
                                    {dq.incorpDateMissing > 0 && (
                                      <Tooltip title={`${dq.incorpDateMissing} missing incorporation dates`}>
                                        <Chip label={dq.incorpDateMissing} size="small" color="warning" sx={{ minWidth: 32, height: 20 }} />
                                      </Tooltip>
                                    )}
                                    {dq.ownershipPctIssue > 0 && (
                                      <Tooltip title={`${dq.ownershipPctIssue} ownership % issues`}>
                                        <Chip label={dq.ownershipPctIssue} size="small" color="info" sx={{ minWidth: 32, height: 20 }} />
                                      </Tooltip>
                                    )}
                                  </Stack>
                                )}
                                <Chip
                                  label={item.count}
                                  size="small"
                                  sx={{
                                    bgcolor: jurisdictionColor,
                                    color: '#fff',
                                    fontWeight: 'bold',
                                    minWidth: 48,
                                  }}
                                />
                              </Box>
                            </Box>
                          </Grid>
                        );
                      })}
                  </Grid>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default Dashboard;
